/**
 * SplashScreen — cold-launch splash for Orbit web.
 *
 * SPLASH-3 — the three splash screens from Rennan's "Orbit iOS Splash
 * Screens" (Oct 12): Basic, Halloween and Christmas, a static hold on
 * the board's own composition, then the hyperspace jump out
 * (APPSTORE-LAUNCH-3) — the exit was never on the boards and stays the
 * transition into the app: the stars streak outward into colour, a
 * white bloom peaks, onComplete(). Mirrors
 * `ios/Orbit/Orbit/Features/Splash/SplashView.swift`; the two are kept
 * in lockstep on purpose.
 *
 * The season decides the board — `useSeason()`: Halloween all October,
 * Christmas December 1–26, Basic otherwise or with Seasonal themes off
 * — the same rule the shell's sky and Nova follow.
 *
 * Geometry. The boards are 390 × 844. The frame is scaled to the
 * viewport (min(width ÷ 390, height ÷ 844), held between 0.85 and 1.5)
 * and centred, so a phone is the board edge to edge and a desktop gets
 * it at up to 1.5× with the sky filling the margins. Two layers are
 * anchored instead of centred: the Christmas aurora is pinned to the top
 * and stretched to the width, its treeline pinned to the bottom and
 * tiled across. Every star, bat, ray and path comes from splashDesign,
 * generated from the design file — nothing here is eyeballed.
 *
 * The jump. The board's stars are converted to an angle and a radius
 * from the centre, so they streak exactly as the old field did. The
 * boards place far fewer stars than the old field, so warp-only stars —
 * invisible at rest — join the burst, and the scenery (moon, aurora,
 * trees, sparkle, glow) fades out under the streaks. Timing is
 * unchanged: a 900 ms hold, warping flips (400 ms), the bloom 260 ms
 * in, the hand-off 180 ms after that, then a 300 ms cross-fade.
 *
 * useSplashScreen below (route-skip list, once-per-tab-session gate)
 * is unchanged.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import orbitLogoWhite from "@/assets/orbit-retro-logo-white.png";
import orbitLogoBlack from "@/assets/orbit-retro-logo-black.png";
import { isClientFacingSurface } from "@/lib/hostRouting";
import { useSeason } from "@/hooks/useSeasonalThemes";
import type { Season } from "@/lib/seasons";
import {
  AURORA_BAND_1,
  AURORA_BAND_2,
  AURORA_RAYS,
  BASIC_STARS,
  BAT_PATH,
  BATS,
  CHRISTMAS_FLAKES,
  CHRISTMAS_STARS,
  FRAME,
  HALLOWEEN_STARS,
  SPARKLE_LINES,
  TREELINE_BACK,
  TREELINE_FRONT,
  type Dot,
} from "./splashDesign";

interface SplashScreenProps {
  onComplete: () => void;
}

// Timing — mirrors SplashView.swift's .task sequence:
//   sleep(900ms) -> warping=true -> sleep(260ms) -> flash=true
//   -> sleep(180ms) -> onFinish()
const HOLD_MS = 900;
const FLASH_DELAY_MS = 260; // after warping flips, when the bloom fires
const FINISH_DELAY_MS = 180; // after the bloom fires, when we hand off
const WARP_TRANSITION_MS = 400; // CSS mirror of SwiftUI's .easeIn(duration: 0.40)
const FLASH_TRANSITION_MS = 160; // CSS mirror of SwiftUI's .easeOut(duration: 0.16)
const WARP_STREAK_PX = 320; // how far a star stretches during the jump — matches iOS
// The handoff cross-fade, matching OrbitApp.swift's
// withAnimation(.easeOut(duration: 0.3)) on .transition(.opacity).
const EXIT_MS = 300;

// A full-screen radial streak burst plus a white flash is close to a
// worked example of what prefers-reduced-motion exists for, and unlike
// iOS the web has a real signal for it. Honour it: same composition,
// same duration, no warp and no bloom — the mark simply holds and fades.
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Aurora-adjacent palette the streaks draw from once warping starts —
// the same six colours as the Swift file's `palette`, so the burst
// reads as Orbit rather than generic sci-fi.
const STAR_PALETTE = [
  "rgb(128, 184, 255)",
  "rgb(179, 135, 255)",
  "rgb(107, 224, 242)",
  "rgb(255, 135, 173)",
  "rgb(255, 194, 112)",
  "#ffffff",
];

/** The old field's density: 170 stars over one phone. */
const FIELD_COUNT = 170;

// ── The board on the viewport ───────────────────────────────────────

interface Frame {
  w: number;
  h: number;
  scale: number;
  ox: number;
  oy: number;
}

function frameFor(w: number, h: number): Frame {
  const fit = Math.min(w / FRAME.w, h / FRAME.h);
  const scale = Math.min(Math.max(fit, 0.85), 1.5);
  return { w, h, scale, ox: (w - FRAME.w * scale) / 2, oy: (h - FRAME.h * scale) / 2 };
}

// ── Stars ──────────────────────────────────────────────────────────

interface Star {
  angleDeg: number;
  startRadius: number;
  size: number;
  restColor: string;
  restOpacity: number; // 0 for a warp-only star
  warpColor: string;
  warpOpacity: number;
}

function boardStars(season: Season | null): Dot[] {
  switch (season) {
    case "halloween":
      return HALLOWEEN_STARS;
    case "christmas":
      return [...CHRISTMAS_STARS, ...CHRISTMAS_FLAKES];
    default:
      return BASIC_STARS;
  }
}

function restColor(season: Season | null, gold?: boolean): string {
  if (gold) return "#F2D48A";
  return season === "halloween" ? "#F3D9A8" : "#ffffff";
}

const pick = () => STAR_PALETTE[Math.floor(Math.random() * STAR_PALETTE.length)];

/**
 * The board's dots, placed where the board puts them and converted to
 * an angle and a radius from the centre; then warp-only extras out to
 * the viewport's corners, sized to hold the old field's density.
 */
function makeStars(season: Season | null, frame: Frame): Star[] {
  const cx = frame.w / 2;
  const cy = frame.h / 2;
  const stars: Star[] = boardStars(season).map((dot) => {
    const x = frame.ox + dot.x * frame.scale;
    const y = frame.oy + dot.y * frame.scale;
    const dx = x - cx;
    const dy = y - cy;
    return {
      angleDeg: (Math.atan2(dy, dx) * 180) / Math.PI,
      startRadius: Math.max(6, Math.hypot(dx, dy)),
      size: Math.max(1, dot.r * 2 * frame.scale),
      restColor: restColor(season, dot.gold),
      restOpacity: dot.o,
      warpColor: pick(),
      warpOpacity: Math.max(dot.o, 0.5),
    };
  });
  // Beyond the board — the margins a desktop or an iPad has around it —
  // the sky carries on at the board's own density, with the board's own
  // sizes and opacities, so a wide screen isn't a phone's sky on black.
  const board = boardStars(season);
  const boardArea = FRAME.w * frame.scale * (FRAME.h * frame.scale);
  const marginArea = Math.max(0, frame.w * frame.h - boardArea);
  const marginCount = Math.round((board.length * marginArea) / boardArea);
  const inBoard = (x: number, y: number) =>
    x >= frame.ox && x <= frame.ox + FRAME.w * frame.scale && y >= frame.oy && y <= frame.oy + FRAME.h * frame.scale;
  for (let i = 0, tries = 0; i < marginCount && tries < marginCount * 20; tries++) {
    const x = Math.random() * frame.w;
    const y = Math.random() * frame.h;
    if (inBoard(x, y)) continue;
    const like = board[Math.floor(Math.random() * board.length)];
    const dx = x - cx;
    const dy = y - cy;
    stars.push({
      angleDeg: (Math.atan2(dy, dx) * 180) / Math.PI,
      startRadius: Math.max(6, Math.hypot(dx, dy)),
      size: Math.max(1, like.r * 2 * frame.scale),
      restColor: restColor(season),
      restOpacity: like.o,
      warpColor: pick(),
      warpOpacity: Math.max(like.o, 0.5),
    });
    i++;
  }
  const maxRadius = Math.hypot(frame.w, frame.h) / 2;
  const density = FIELD_COUNT / (620 * 620);
  const wanted = Math.max(150, Math.min(500, Math.round(density * maxRadius * maxRadius)));
  for (let i = stars.length; i < wanted; i++) {
    stars.push({
      angleDeg: Math.random() * 360,
      startRadius: 10 + Math.random() * (maxRadius - 10),
      size: 1 + Math.random() * 1.6,
      restColor: "#ffffff",
      restOpacity: 0,
      warpColor: pick(),
      warpOpacity: 0.35 + Math.random() * 0.55,
    });
  }
  return stars;
}

// ── Sky ────────────────────────────────────────────────────────────

function skyGradient(season: Season | null): string {
  switch (season) {
    case "halloween":
      return "linear-gradient(180deg, #1A0F2E 0%, #120920 55%, #0B0612 100%)";
    case "christmas":
      return "linear-gradient(180deg, #04081A 0%, #071530 42%, #0A1B33 72%, #0C2238 100%)";
    default:
      return "linear-gradient(180deg, #1B2544 0%, #131A30 52%, #110D22 100%)";
  }
}

// ── Scenery — the board without its stars and mark ──────────────────

/** Basic: the gold glow and the sparkle, top right. */
function BasicScenery({ frame }: { frame: Frame }) {
  const gx = frame.ox + 195 * frame.scale;
  const gy = frame.oy + 387 * frame.scale;
  const gr = 180 * frame.scale;
  const sx = frame.ox + 318 * frame.scale;
  const sy = frame.oy + 240 * frame.scale;
  return (
    <svg className="absolute inset-0" width={frame.w} height={frame.h} aria-hidden="true">
      <defs>
        <radialGradient id="splash-basic-glow">
          <stop offset="0" stopColor="rgb(227,179,77)" stopOpacity="0.13" />
          <stop offset="1" stopColor="rgb(227,179,77)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={gx} cy={gy} r={gr} fill="url(#splash-basic-glow)" />
      <g
        transform={`translate(${sx} ${sy}) scale(${frame.scale})`}
        stroke="#9FB2E4"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
        opacity="0.28"
      >
        {SPARKLE_LINES.map(([x1, y1, x2, y2], i) => (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
        ))}
      </g>
    </svg>
  );
}

/** Halloween: the moon with its glow and shade, three bats. */
function HalloweenScenery({ frame }: { frame: Frame }) {
  const d = 300 * frame.scale;
  const left = frame.ox + 45 * frame.scale;
  const top = frame.oy + 262 * frame.scale;
  return (
    <>
      <div
        className="absolute rounded-full"
        style={{
          left,
          top,
          width: d,
          height: d,
          background:
            "radial-gradient(circle at 38% 34%, #FBE3A6 0%, #F2BE63 55%, #E0893C 100%)",
          boxShadow:
            `0 0 ${90 * frame.scale}px ${30 * frame.scale}px rgba(224,137,60,0.32), ` +
            `0 0 ${200 * frame.scale}px ${80 * frame.scale}px rgba(224,137,60,0.14), ` +
            `inset ${-18 * frame.scale}px ${-24 * frame.scale}px ${60 * frame.scale}px rgba(160,70,20,0.28)`,
        }}
      />
      {BATS.map((b, i) => (
        <svg
          key={i}
          viewBox="0 0 120 48"
          width={b.w * frame.scale}
          height={b.h * frame.scale}
          aria-hidden="true"
          className="absolute"
          style={{
            left: frame.ox + b.x * frame.scale,
            top: frame.oy + b.y * frame.scale,
            transform: `rotate(${b.rot}deg)`,
            opacity: b.o,
          }}
        >
          <path fill="#0B0612" d={BAT_PATH} />
        </svg>
      ))}
    </>
  );
}

/**
 * Christmas: the aurora pinned to the top and stretched to the width;
 * the treeline pinned to the bottom and tiled across. Both are the
 * board's own SVG.
 */
function ChristmasScenery({ frame }: { frame: Frame }) {
  const tile = FRAME.w * frame.scale;
  const bottom = frame.h - FRAME.h * frame.scale;
  const first = Math.floor(frame.ox / tile) - 1;
  const tiles: number[] = [];
  for (let k = first; k * tile + frame.ox < frame.w; k++) tiles.push(k);
  return (
    <>
      {/* The sky, in the board's coordinates: x stretched to the
          viewport, y at the board's scale, from the top. */}
      <svg
        className="absolute left-0 top-0"
        width={frame.w}
        height={FRAME.h * frame.scale}
        viewBox={`0 0 ${FRAME.w} ${FRAME.h}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="splash-xmas-band" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#7A5BD8" stopOpacity="0" />
            <stop offset="0.32" stopColor="#4A8FD6" stopOpacity="0.2" />
            <stop offset="0.7" stopColor="#2ED39A" stopOpacity="0.62" />
            <stop offset="0.94" stopColor="#7CF5BD" stopOpacity="0.95" />
            <stop offset="1" stopColor="#7CF5BD" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="splash-xmas-band2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3A6FD8" stopOpacity="0" />
            <stop offset="0.6" stopColor="#28B8B0" stopOpacity="0.32" />
            <stop offset="1" stopColor="#5EE6C0" stopOpacity="0.55" />
          </linearGradient>
          <linearGradient id="splash-xmas-ray" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#D06AC8" stopOpacity="0" />
            <stop offset="0.22" stopColor="#B070E0" stopOpacity="0.18" />
            <stop offset="0.55" stopColor="#3FD9A0" stopOpacity="0.38" />
            <stop offset="1" stopColor="#B2FFD9" stopOpacity="0.9" />
          </linearGradient>
          <filter id="splash-xmas-blur-band" x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation="14" />
          </filter>
          <filter id="splash-xmas-blur-band2" x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation="22" />
          </filter>
          <filter id="splash-xmas-blur-ray" x="-50%" y="-20%" width="200%" height="140%">
            <feGaussianBlur stdDeviation="3.5" />
          </filter>
        </defs>
        <path d={AURORA_BAND_1} fill="url(#splash-xmas-band)" filter="url(#splash-xmas-blur-band)" />
        <g filter="url(#splash-xmas-blur-ray)">
          {AURORA_RAYS.map((r, i) => (
            <rect
              key={i}
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              fill="url(#splash-xmas-ray)"
              opacity={r.o}
              transform={`rotate(${r.rot} ${r.px} ${r.py})`}
            />
          ))}
        </g>
        <path
          d={AURORA_BAND_2}
          fill="url(#splash-xmas-band2)"
          filter="url(#splash-xmas-blur-band2)"
          opacity="0.55"
        />
      </svg>

      {/* The treeline, from the bottom, tiled. */}
      <svg className="absolute inset-0" width={frame.w} height={frame.h} aria-hidden="true">
        <defs>
          <g id="splash-xmas-trees">
            <path d={TREELINE_BACK} fill="#0B1830" />
            <path d={TREELINE_FRONT} fill="#040913" />
          </g>
        </defs>
        {tiles.map((k) => (
          <use
            key={k}
            href="#splash-xmas-trees"
            transform={`translate(${frame.ox + k * tile} ${bottom}) scale(${frame.scale})`}
          />
        ))}
        <rect
          x={0}
          y={bottom + 812 * frame.scale}
          width={frame.w}
          height={Math.max(0, frame.h - bottom - 812 * frame.scale)}
          fill="#040913"
        />
      </svg>
    </>
  );
}

// ── The splash ──────────────────────────────────────────────────────

const SplashScreen = ({ onComplete }: SplashScreenProps) => {
  const season = useSeason();
  const [warping, setWarping] = useState(false);
  const [flash, setFlash] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const calm = useMemo(prefersReducedMotion, []);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Sized once per mount — the splash lasts 1.6 s; a resize mid-way
  // isn't worth re-laying out for.
  const frame = useMemo(
    () => frameFor(typeof window === "undefined" ? FRAME.w : window.innerWidth, typeof window === "undefined" ? FRAME.h : window.innerHeight),
    [],
  );
  // Generated once per mount, like the Swift file computing `stars`
  // once in `init`.
  const stars = useMemo(() => makeStars(season, frame), [season, frame]);

  useEffect(() => {
    const leaveAt = HOLD_MS + FLASH_DELAY_MS + FINISH_DELAY_MS;
    // Under reduced motion the beats still exist, they just do not fire
    // the warp or the bloom — the hold runs its full length and hands off
    // on the same clock, so nothing downstream has to know the difference.
    const timers = [
      calm ? null : window.setTimeout(() => setWarping(true), HOLD_MS),
      calm ? null : window.setTimeout(() => setFlash(true), HOLD_MS + FLASH_DELAY_MS),
      window.setTimeout(() => setLeaving(true), leaveAt),
      window.setTimeout(() => onCompleteRef.current?.(), leaveAt + EXIT_MS),
    ];
    return () => timers.forEach((t) => t !== null && window.clearTimeout(t));
  }, [calm]);

  // The mark where the board puts it: Basic a touch above centre,
  // Halloween in black on the moon, Christmas just below centre. The
  // asset is 1920 × 1358; the board's top edge plus half that height at
  // the board's width is its centre.
  const aspect = 1358 / 1920;
  const mark =
    season === "halloween"
      ? { src: orbitLogoBlack, width: 212, cy: 330 + (212 * aspect) / 2, opacity: 0.9 }
      : season === "christmas"
        ? { src: orbitLogoWhite, width: 200, cy: 358 + (200 * aspect) / 2, opacity: 1 }
        : { src: orbitLogoWhite, width: 200, cy: 422 - 36, opacity: 1 };

  const streak = WARP_STREAK_PX * Math.max(1, frame.scale);

  return (
    <div
      className="fixed inset-0 z-[99999] overflow-hidden isolate"
      style={{
        backgroundImage: skyGradient(season),
        // the cross-fade iOS gets from .transition(.opacity)
        opacity: leaving ? 0 : 1,
        transition: `opacity ${EXIT_MS}ms ease-out`,
        pointerEvents: leaving ? "none" : undefined,
      }}
      aria-label="Orbit loading"
      role="status"
    >
      {/* The board's scenery — everything but the stars and the mark —
          goes out under the streaks. */}
      <div
        className="absolute inset-0"
        style={{
          opacity: warping ? 0 : 1,
          transition: `opacity ${WARP_TRANSITION_MS}ms ease-in`,
        }}
      >
        {season === "halloween" ? (
          <HalloweenScenery frame={frame} />
        ) : season === "christmas" ? (
          <ChristmasScenery frame={frame} />
        ) : (
          <BasicScenery frame={frame} />
        )}
      </div>

      {/* Starfield — the board's own dots in the board's own places,
          still through the hold. Each star stretches into a coloured
          streak along its own radial direction and flies outward once
          warping flips; the warp-only extras appear with it. */}
      <div className="absolute inset-0">
        {stars.map((star, i) => {
          const currentLen = warping ? streak : star.size;
          const centerRadius = star.startRadius + currentLen / 2;
          return (
            <div
              key={i}
              className="absolute left-1/2 top-1/2"
              style={{
                // Fixed box, stretched by scaleX: reaches the geometry on
                // the compositor instead of transitioning `width` on a
                // few hundred elements at once.
                width: streak,
                height: star.size,
                borderRadius: star.size,
                opacity: warping ? star.warpOpacity : star.restOpacity,
                backgroundColor: warping ? star.warpColor : star.restColor,
                transform:
                  `translate(-50%, -50%) rotate(${star.angleDeg}deg) ` +
                  `translateX(${centerRadius}px) scaleX(${currentLen / streak})`,
                transition:
                  `transform ${WARP_TRANSITION_MS}ms ease-in, ` +
                  `background-color ${WARP_TRANSITION_MS}ms ease-in, ` +
                  `opacity ${WARP_TRANSITION_MS}ms ease-in`,
              }}
            />
          );
        })}
      </div>

      {/* The mark — fades out and scales up as the jump starts, same
          as the Swift file. */}
      <img
        src={mark.src}
        alt="Orbit"
        className="absolute"
        style={{
          width: mark.width * frame.scale,
          height: "auto",
          left: frame.ox + 195 * frame.scale,
          top: frame.oy + mark.cy * frame.scale,
          opacity: warping ? 0 : mark.opacity,
          transform: `translate(-50%, -50%) scale(${warping ? 1.2 : 1})`,
          transition: `opacity ${WARP_TRANSITION_MS}ms ease-in, transform ${WARP_TRANSITION_MS}ms ease-in`,
        }}
      />

      {/* The arrival bloom — a quick white flash right as the jump
          completes, timed in the effect above. Stays pure white even
          though the streaks under it are colourful, same as iOS. */}
      <div
        className="absolute inset-0 bg-white pointer-events-none"
        style={{
          opacity: flash ? 0.85 : 0,
          transition: `opacity ${FLASH_TRANSITION_MS}ms ease-out`,
        }}
      />
    </div>
  );
};

/**
 * Hook deciding whether to show the splash. Splash on the first
 * app open of a tab session (sessionStorage-gated) so route changes
 * — or any unintended AppContent remount — don't re-fire it. Public
 * client portals + embedded forms always skip.
 *
 * The previous implementation relied on AppContent never unmounting
 * across route changes, but in practice some route transitions do
 * remount AppContent (e.g. switching between auth-gated and public
 * routes), which made the splash flash on every other navigation.
 * Persisting a "shown" flag in sessionStorage fixes that without
 * losing the per-tab-open behavior.
 */
const SPLASH_SHOWN_KEY = "orbit.splash.shownThisSession";

export const useSplashScreen = () => {
  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window === "undefined") return false;

    // Skip splash entirely for any public surface — clients
    // visiting a portal / signed contract / booking link should
    // never see Orbit's brand splash.
    const pathname = window.location.pathname;
    //
    // GAL-SPLASH — this list is the ONLY thing standing between a
    // photographer's client and 1.6 seconds of Orbit's starfield, and
    // it had drifted out of sync with the router. Every route in
    // App.tsx wrapped in <CustomDomainProvider> is a public surface;
    // as of this audit that is:
    //
    //   /book/:slug   /d/:token        /delivery/:slug   /gallery/demo
    //   /gallery/:id  /portal/:token   /sign/:token      /video/:token
    //
    // `/gallery/` and `/video/` were missing, so the two surfaces a
    // studio shares with clients most often — the proof gallery and
    // the video proofing link — opened on Orbit's brand splash. For a
    // product whose whole promise is "under your own brand, not ours",
    // that is the worst possible place to leak it.
    //
    // This cannot read the route table: the hook runs off
    // window.location.pathname before BrowserRouter mounts, so a
    // literal list is unavoidable. IF YOU ADD A PUBLIC ROUTE, ADD ITS
    // PREFIX HERE — there is a matching note beside the public routes
    // in App.tsx.
    //
    // SITE-BRAND-1 — the list moved to hostRouting.ts
    // (CLIENT_FACING_PREFIXES) and now also covers published sites
    // (`/s/`, `/site-preview`) and every path on a studio's custom
    // domain, which opened on this splash for every first visit.
    if (isClientFacingSurface(pathname)) return false;

    // NOVA-ENV-1 — /nova/environment is a marketing page (the green
    // button under Nova's cards on /orbit opens it). /orbit skips the
    // splash only because it is a single segment (the rule below);
    // this one has two and "nova" is a known app route, so it is
    // named. A visitor arriving on a shared link should get the page,
    // not 1.6 seconds of the app's starfield first. (Lower-cased: the
    // router matches the path whatever its case.)
    if (pathname.replace(/\/+$/, "").toLowerCase() === "/nova/environment") return false;

    // Single-segment unknown slug means it's a public org-slug
    // intake form. Reserved internal route names are listed here
    // explicitly so we don't ALSO skip splash for those.
    const knownTopLevels = new Set([
      "auth", "terms", "privacy", "admin", "projects", "clients",
      "settings", "assistant", "billing", "calendar", "deliveries",
      "nova", "onboarding", "studio", "intake", "inquiries", "m",
      // Added so /forms and /templates (which redirect to the
      // settings page now — see App.tsx) get a normal in-app
      // splash treatment instead of being skipped as if they
      // were public org slugs.
      "forms", "templates",
    ]);
    const segments = pathname.split("/").filter(Boolean);

    // GOOGIE-FROST-1 — no splash on the MARKETING page. Rennan: "NO
    // splash screen on marketing page or ANY galleries." Galleries were
    // already covered (`/gallery/` is in CLIENT_FACING_PREFIXES) and so
    // is `/orbit` — it is a single segment, which the rule below skips.
    // What was left is `/`, where a SIGNED-OUT visitor gets marketing:
    // the brochure opening on 1.6 seconds of the app's starfield.
    //
    // The session is read straight out of storage because this runs in
    // a useState initializer, before BrowserRouter and well before
    // useAuth has resolved anything. Supabase keys its session
    // `sb-<project-ref>-auth-token`, and the ref is matched by shape
    // rather than hard-coded so this cannot rot if the project moves.
    // A signed-in studio landing on `/` still gets the splash — that is
    // the app opening, which is what the splash is for.
    if (segments.length === 0) {
      let signedIn = false;
      try {
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) {
            signedIn = true;
            break;
          }
        }
      } catch {
        // Storage unavailable (private browsing). Treat it as signed
        // out: showing the brochure without a splash is the smaller
        // mistake of the two.
      }
      if (!signedIn) return false;
    }

    if (segments.length === 1 && !knownTopLevels.has(segments[0])) return false;

    // sessionStorage gate — once we've shown splash this tab
    // session, never again, even if AppContent remounts.
    try {
      if (window.sessionStorage.getItem(SPLASH_SHOWN_KEY) === "1") return false;
    } catch {
      // sessionStorage unavailable (private browsing, etc.) — fall
      // through and show. Better to over-show splash than to never
      // show it; the user can dismiss with the auto-fade.
    }

    return true;
  });

  const handleComplete = () => {
    // Written once, through the constant. This wrote the same value twice,
    // once via the constant and once via a literal copy of it — harmless
    // while they agreed and a trap the moment anyone changed one of them.
    try {
      window.sessionStorage.setItem(SPLASH_SHOWN_KEY, "1");
    } catch {
      // Ignore — see the read side above.
    }
    setShowSplash(false);
  };

  return { showSplash, handleComplete };
};

export default SplashScreen;
